import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import { useState } from "react";

export type TitleAccountFormValues = {
  name: string;
  company_name: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
};

export function titleAccountFormValues(account: {
  name?: string | null;
  company_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}): TitleAccountFormValues {
  return {
    name: account.name ?? "",
    company_name: account.company_name ?? "",
    email: account.email ?? "",
    phone: account.phone ?? "",
    address1: account.address1 ?? "",
    address2: account.address2 ?? "",
    city: account.city ?? "",
    state: account.state ?? "",
    zipcode: account.zipcode ?? "",
  };
}

export function missingTitleProfileFields(
  values: TitleAccountFormValues,
  isSecondary: boolean,
): string[] {
  const missing = [
    !values.name.trim() ? "Name" : null,
    !values.phone.trim() ? "Phone" : null,
  ];
  if (!isSecondary) {
    missing.push(
      !values.address1.trim() ? "Address" : null,
      !values.city.trim() ? "City" : null,
      !values.state.trim() ? "State" : null,
      !values.zipcode.trim() ? "ZIP" : null,
    );
  }
  return missing.filter((field): field is string => field !== null);
}

export function TitleAccountForm({
  initial,
  isSecondary,
  submitLabel,
  busy,
  onSubmit,
}: {
  initial: TitleAccountFormValues;
  isSecondary: boolean;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: TitleAccountFormValues) => Promise<void> | void;
}) {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [placesInput, setPlacesInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  const setField = (key: keyof TitleAccountFormValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        const missing = missingTitleProfileFields(values, isSecondary);
        if (missing.length > 0) {
          setError(`Enter ${missing.join(", ")}.`);
          return;
        }
        setError(null);
        void onSubmit(values);
      }}
    >
      <div className="grid gap-6 md:grid-cols-2">
        <Field
          label="Name"
          value={values.name}
          onChange={(value) => setField("name", value)}
        />
        <Field
          label="Company"
          value={values.company_name}
          onChange={(value) => setField("company_name", value)}
          readOnly={isSecondary}
        />
        <Field label="Email" type="email" value={values.email} readOnly />
        <Field
          label="Phone"
          value={values.phone}
          onChange={(value) => setField("phone", value)}
        />
        <div className="space-y-2 md:col-span-2">
          <Label className="font-bold" htmlFor="title-account-address">
            Address
          </Label>
          <Input
            id="title-account-address"
            value={values.address1}
            readOnly={isSecondary}
            onChange={(event) => {
              setField("address1", event.target.value);
              setPlacesInput(event.target.value);
            }}
          />
          {!isSecondary && suggestions.length > 0 ? (
            <ul className="rounded-md border bg-background">
              {suggestions.map((suggestion) => {
                const text =
                  suggestion.placePrediction?.text?.text ?? "Address";
                return (
                  <li key={suggestion.placePrediction?.placeId ?? text}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                      onClick={() => {
                        void (async () => {
                          const place = suggestion.placePrediction?.toPlace();
                          if (!place) return;
                          await place.fetchFields({
                            fields: ["addressComponents"],
                          });
                          if (!place.addressComponents) return;
                          const parsed = parseGoogleAddressComponents(
                            place.addressComponents,
                          );
                          setValues((current) => ({
                            ...current,
                            address1: parsed.address1,
                            address2: parsed.address2,
                            city: parsed.city,
                            state: parsed.state,
                            zipcode: parsed.zipcode,
                          }));
                          setPlacesInput("");
                        })();
                      }}
                    >
                      {text}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        <Field
          label="Address line 2"
          value={values.address2}
          onChange={(value) => setField("address2", value)}
          readOnly={isSecondary}
        />
        <Field
          label="City"
          value={values.city}
          onChange={(value) => setField("city", value)}
          readOnly={isSecondary}
        />
        <Field
          label="State"
          value={values.state}
          onChange={(value) => setField("state", value)}
          readOnly={isSecondary}
        />
        <Field
          label="ZIP"
          value={values.zipcode}
          onChange={(value) => setField("zipcode", value)}
          readOnly={isSecondary}
        />
      </div>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
      <Button type="submit" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  readOnly,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  type?: string;
  required?: boolean;
  readOnly?: boolean;
}) {
  const id = `title-account-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className="space-y-2">
      <Label className="font-bold" htmlFor={id}>
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        required={required}
        readOnly={readOnly}
        onChange={(event) => onChange?.(event.target.value)}
      />
    </div>
  );
}
